import { datadogRum, type RumEvent } from "@datadog/browser-rum";
import { type ShowcaseToken } from "@mg/schemas/src/prince/auth";
import { jwtDecode } from "jwt-decode";
import { useEffect } from "react";

import { DD_CLIENT_TOKEN, DD_HOST, DD_APP_ID, DD_VERSION } from "./env";
import { scrubSessionQuery } from "./scrubSessionQuery";

import { FETCH_NETWORK_ERROR_MESSAGES } from "../utils/fetchNetworkError";

// why: React 18's production error logger runs console.error(<thrown value>) for every error a route boundary catches, and smartFetch throws the raw Response (utils/http.ts). Datadog serialises a non-Error, non-string console argument from Object.prototype.toString, so that RUM event is the bare placeholder `"[Response]"` — no error stack, only the handling stack of React's logger call site, no status, no URL — and a single one pages the prod RUM monitor. A label in front (console.error("Failed to check review status:", response) → `Failed to check review status: "[Response]"`) names a call site but is just as unactionable and pages just the same (#6784), so the placeholder may follow whitespace; it must still end the message, so readable text after it keeps the event.
const SERIALIZED_TYPE_PLACEHOLDER = /(?:^|\s)"\[[A-Za-z][A-Za-z0-9]*\]"$/;

/**
 * A `console`-sourced RUM error whose logged value carried nothing readable: not
 * a string, not an Error, so Datadog replaced it with a type placeholder that
 * ends the message, alone or after a label. Console errors that carry an error
 * stack, or readable text after the placeholder, are kept — this drops only the
 * events an engineer cannot act on.
 */
export function isUnreadableConsoleRumError(event: RumEvent): boolean {
  if (event.type !== "error") return false;

  const { message, source, stack } = event.error;
  // A stack means an Error reached console.error, so the event is readable.
  if (source !== "console" || stack != null) return false;

  return SERIALIZED_TYPE_PLACEHOLDER.test(message);
}

// why: a browser extension's content script is fetched by the extension's own origin under its own CSP, so carrie can never load it and no carrie change can make it load. Its failure carries a full stack, which the unreadable-console predicate above keeps, and pages this monitor once (#7446). Keyed on the extension scheme, not on `Failed to fetch dynamically imported module`: carrie does lazily import, so a phrase filter would swallow a real chunk-load failure — the one signal this monitor exists for.
const EXTENSION_SCHEME = /(?:chrome|moz|safari-web)-extension:\/\//;

/**
 * A RUM error whose message names a URL under a browser-extension scheme: the
 * failing resource belongs to a third party, so nothing in carrie can repair it
 * and the on-call engineer has no action. Reads the message only — a stack that
 * merely ends in an extension frame is a carrie-originated throw and is kept.
 */
export function isExtensionSourcedRumError(event: RumEvent): boolean {
  if (event.type !== "error") return false;

  return EXTENSION_SCHEME.test(event.error.message);
}

// why: React logs every error a route boundary catches with console.error, before carrie's error page renders, so a dropped connection reaching a boundary paged on-call with no fault to fix (#8153). Matched on the whole message, so "Failed to fetch dynamically imported module" - a real chunk-load failure - still pages.
const NETWORK_TYPE_ERROR_MESSAGES = new Set(
  Array.from(FETCH_NETWORK_ERROR_MESSAGES).map(
    (message) => `TypeError: ${message}`,
  ),
);

/** A console-sourced RUM error that is exactly the TypeError fetch throws when the network is gone. */
export function isDroppedConnectionRumError(event: RumEvent): boolean {
  if (event.type !== "error") return false;

  const { message, source } = event.error;
  return source === "console" && NETWORK_TYPE_ERROR_MESSAGES.has(message);
}

// why: Chrome reports an intervention when react-dom's touchend listener calls preventDefault() during a scroll; browser-core turns it into a source 'report' RUM error and one scroll-tap paged on-call (#8045). Keyed on the intervention id, never on source 'report' alone: CSP violations arrive with the same source and must still page.
const IGNORED_EVENT_CANCEL = "IgnoredEventCancel";
const INTERVENTION_CANCEL_PREFIX = "intervention: Ignored attempt to cancel";

/** A RUM error built from Chrome's ignored-cancel intervention report. */
export function isBrowserInterventionRumError(event: RumEvent): boolean {
  if (event.type !== "error") return false;

  const { message, source, type } = event.error;
  if (source !== "report") return false;
  return (
    type === IGNORED_EVENT_CANCEL ||
    message.startsWith(INTERVENTION_CANCEL_PREFIX)
  );
}

export function useDatadog() {
  useEffect(() => {
    if (DD_CLIENT_TOKEN && DD_APP_ID) {
      datadogRum.init({
        applicationId: DD_APP_ID,
        clientToken: DD_CLIENT_TOKEN,
        site: "datadoghq.com",
        service: "CARRIE",
        env: DD_HOST,
        version: DD_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        enableExperimentalFeatures: ["clickmap"],
        allowedTracingUrls: [
          (url) =>
            !!url.match(
              /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*puntt\.ai(\/[^\s?]*)(\?[^\s]*)?$/,
            ),
        ],
        beforeSend: (event) => {
          if (event.view?.url) {
            event.view.url = scrubSessionQuery(event.view.url);
          }
          return (
            !isUnreadableConsoleRumError(event) &&
            !isExtensionSourcedRumError(event) &&
            !isDroppedConnectionRumError(event) &&
            !isBrowserInterventionRumError(event)
          );
        },
      });

      datadogRum.startSessionReplayRecording();

      // why: storage reads throw SecurityError rather than returning null when
      // storage is blocked (private mode, third-party-cookie blocking). This runs
      // in the RUM-init effect, so an unguarded read aborts app boot instead of
      // merely skipping user identification.
      let localToken: string | null = null;
      try {
        localToken = localStorage.getItem("token");
      } catch {
        localToken = null;
      }

      if (localToken) {
        try {
          const decodedToken = jwtDecode<ShowcaseToken>(localToken);
          // why: RUM gets the id alone — the decoded JWT carries email, name, permissions and impersonatorEmail, which must not reach RUM or session replay
          datadogRum.setUser({ id: decodedToken.userID });
        } catch {
          // Corrupt/expired token — skip RUM user identification
        }
      }
    }
  }, []);

  return null;
}
